import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RefreshCw, CheckCircle, XCircle, Coins, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const VerifyMagicLinkPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, isAuthenticated } = useAuth();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const hasVerified = useRef(false);
  const token = searchParams.get('token');

  useEffect(() => {
    // If already authenticated, redirect to dashboard
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
      return;
    }

    // Prevent double verification in StrictMode
    if (hasVerified.current) return;
    hasVerified.current = true;

    const verifyToken = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage('Invalid link. No token found.');
        return;
      }

      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/verify-magic-link`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setStatus('success');
          setUser(data.user);
          
          // Store session token in localStorage as fallback for cookies
          if (data.session_token) {
            localStorage.setItem('session_token', data.session_token);
          }
          
          // Redirect to dashboard after brief success message
          setTimeout(() => {
            navigate('/dashboard', { 
              replace: true,
              state: { user: data.user }
            });
          }, 1500);
        } else {
          setStatus('error');
          // Provide more helpful error messages
          if (data.detail?.includes('already been used')) {
            setErrorMessage('This link has already been used. Please request a new one.');
          } else if (data.detail?.includes('expired')) {
            setErrorMessage('This link has expired. Please request a new one.');
          } else {
            setErrorMessage(data.detail || 'Invalid or expired link. Please request a new one.');
          }
        }
      } catch (err) {
        console.error('Verification error:', err);
        setStatus('error');
        setErrorMessage('Something went wrong. Please try again.');
      }
    };

    verifyToken();
  }, [token, navigate, setUser, isAuthenticated]);

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md text-center"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center">
            <Coins className="w-5 h-5 text-black" />
          </div>
          <span className="text-xl font-bold text-white">
            BuyMe<span className="text-[#D4AF37]">Gold</span>
          </span>
        </div>

        <div className="bg-[#12121a] border border-[#1e1e2e] rounded-2xl p-8">
          {status === 'verifying' && (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center"
              >
                <RefreshCw className="w-8 h-8 text-[#D4AF37]" />
              </motion.div>
              <h2 className="text-2xl font-bold text-white mb-2">Verifying your link</h2>
              <p className="text-gray-400">Please wait while we sign you in...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
                className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-emerald-500/10 flex items-center justify-center"
              >
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </motion.div>
              <h2 className="text-2xl font-bold text-white mb-2">You're signed in!</h2>
              <p className="text-gray-400">Redirecting to your dashboard...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
                className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-red-500/10 flex items-center justify-center"
              >
                <XCircle className="w-8 h-8 text-red-400" />
              </motion.div>
              <h2 className="text-2xl font-bold text-white mb-2">Link expired or invalid</h2>
              <p className="text-gray-400 mb-6">{errorMessage}</p>
              
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full h-11 btn-gold text-black font-semibold rounded-xl flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Request New Link
                </Button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyMagicLinkPage;
